import { DownOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

import { isGroup, type NavEntry, type NavItem } from './navigation';

/**
 * The sidebar's own navigation, built rather than themed.
 *
 * This was an antd `Menu`, and the file that styled it had grown into a column
 * of `!important` overrides fighting rc-menu's inline padding, its accent-tinted
 * selection and its group markup. The states a nav row needs — rest, hover,
 * pressed, current, current-but-folded-away — are five rules; owning them
 * outright is less code than talking antd out of its own.
 *
 * The rules it now keeps:
 *
 *   Colour never marks position. The current row is a raised white card with a
 *   hairline; hover is a flat grey wash. The only green in the chrome is a
 *   primary button and the focus ring, so anything green means "this does
 *   something", never "you are here".
 *
 *   Everything starts on one rail, at `--nav-gutter` from the edge, including
 *   the logo above and the account row below. Children step in from it once.
 */

type Props = {
  entries: NavEntry[];
  /** Sections the user has opened. Controlled, so the route can seed it. */
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
  collapsed?: boolean;
  visible: (item: NavItem) => boolean;
  /**
   * Names the landmark. The sider renders this component twice, and two
   * unnamed `<nav>`s give a screen reader two identical "navigation" entries
   * with no way to tell the work from the administration.
   */
  label: string;
};

/**
 * Longest matching prefix, so /catalog/medicines/:id keeps Medicines current.
 *
 * Undefined when this nav holds none of the current route, which is a state
 * that has to be expressible: the sider renders two of these, and the fallback
 * used to be `'/'`. Dashboard's path IS `'/'`, so standing on /users — a route
 * the main list does not contain — lit Dashboard in one nav while Users & roles
 * was lit in the other. Two current rows, neither of them wrong on its own.
 */
function useCurrentPath(paths: string[]): string | undefined {
  const { pathname } = useLocation();
  const matches = paths
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0];
}

function Row({
  item,
  current,
  nested,
  collapsed,
  onClick,
}: {
  item: NavItem;
  current: boolean;
  nested?: boolean;
  collapsed?: boolean;
  onClick: () => void;
}) {
  const body = (
    <button
      type="button"
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
      className={[
        'nav-row',
        current ? 'nav-row-current' : '',
        nested ? 'nav-row-nested' : '',
        collapsed ? 'nav-row-collapsed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {item.icon ? (
        <span className="nav-icon">
          <item.icon />
        </span>
      ) : null}
      {collapsed ? null : (
        <>
          <span className="nav-label">{item.label}</span>
          {item.stubbed ? (
            <Tag bordered={false} className="nav-tag">
              soon
            </Tag>
          ) : null}
        </>
      )}
    </button>
  );

  // Collapsed, the label is the only thing identifying the row, so it moves to
  // a tooltip rather than disappearing.
  return collapsed ? (
    <Tooltip title={item.label} placement="right">
      {body}
    </Tooltip>
  ) : (
    body
  );
}

export function SidebarNav({
  entries,
  openKeys,
  onOpenChange,
  collapsed,
  visible,
  label,
}: Props) {
  const navigate = useNavigate();
  const paths = entries.flatMap((entry) =>
    isGroup(entry) ? entry.items.map((item) => item.path) : [entry.path],
  );
  const current = useCurrentPath(paths);

  const toggle = (key: string) =>
    onOpenChange(
      openKeys.includes(key) ? openKeys.filter((k) => k !== key) : [...openKeys, key],
    );

  return (
    <nav className="nav" aria-label={label}>
      {entries.map((entry) => {
        if (!isGroup(entry)) {
          return visible(entry) ? (
            <Row
              key={entry.key}
              item={entry}
              current={current === entry.path}
              collapsed={collapsed}
              onClick={() => navigate(entry.path)}
            />
          ) : null;
        }

        const children = entry.items.filter(visible);
        if (children.length === 0) return null;

        const holdsCurrent = children.some((item) => item.path === current);
        const open = collapsed ? false : openKeys.includes(entry.key);

        return (
          <div key={entry.key} className="nav-section">
            <button
              type="button"
              onClick={() => (collapsed ? navigate(children[0]!.path) : toggle(entry.key))}
              aria-expanded={open}
              className={['nav-row', 'nav-row-section', collapsed ? 'nav-row-collapsed' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <span className="nav-icon">
                <entry.icon />
              </span>
              {collapsed ? null : (
                <>
                  <span className="nav-label">{entry.label}</span>
                  {/*
                    * Folded away, the current page is out of sight. The dot says
                    * it is in here — a job the chevron cannot do, since that only
                    * reports whether the section is open.
                    */}
                  {holdsCurrent && !open ? <span className="nav-dot" /> : null}
                  <DownOutlined className={`nav-chevron ${open ? 'nav-chevron-open' : ''}`} />
                </>
              )}
            </button>

            {/*
              * Rendered only when open. Animating height here would mean either
              * measuring the list or fixing a row height, and a nav that
              * stutters open is worse than one that simply is open.
              */}
            {open ? (
              <div className="nav-children">
                {children.map((item) => (
                  <Row
                    key={item.key}
                    item={item}
                    nested
                    current={current === item.path}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
